import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, FileText, Loader2, UploadCloud } from 'lucide-react';
import type { HrDocumentItem, HrDocumentsResponse } from '@ip/shared-types';
import type { AppShellContext } from '@/components/layout/AppShell';
import { HrTabs } from '@/components/hr/HrTabs';
import { useHrMe } from '@/components/hr/useHrData';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useHrApi, SessionExpiredError } from '@/services/api';
import { cn } from '@/lib/utils';
import { formatDateMedium } from '@/lib/hr';
import { sanitizeFileNamePart } from '@/utils/download';
import { t } from '@/i18n';

function todayYmd(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Deadline badge: red once past due, amber inside the last 3 days. */
function DeadlineBadge({ deadline }: { deadline: string | null }) {
  if (!deadline) return null;
  const today = todayYmd();
  const overdue = deadline < today;
  const soon = !overdue && deadline <= new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10);
  return (
    <Badge
      variant="outline"
      className={cn(
        'font-normal whitespace-nowrap',
        overdue && 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-400',
        soon && 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
      )}
    >
      {t('hr.documents.deadline', { date: formatDateMedium(deadline) })}
    </Badge>
  );
}

async function openDownload(
  nip: string,
  item: HrDocumentItem,
  hrDocumentDownload: (nip: string, publicationId: string, fileName: string) => Promise<void>,
) {
  try {
    await hrDocumentDownload(nip, item.publicationId, `${sanitizeFileNamePart(item.number)}.pdf`);
  } catch {
    /* session expired — handled by the global modal */
  }
}

/**
 * HR "Dokumenty" (spec §8.4/Task 7): documents awaiting my acceptance,
 * documents I've already accepted, and the public document library —
 * electronic acceptance re-confirms identity via the same PESEL-last-4
 * mechanism as "Moje dane" (shared brute-force lock).
 */
export function HrDocumentsPage() {
  const { nip } = useOutletContext<AppShellContext>();
  const { hrMe, loading: hrMeLoading } = useHrMe(nip);
  const { hrDocuments, hrDocumentDownload, hrAcceptDocument, uploadSignedDocument } = useHrApi();

  const [data, setData] = useState<HrDocumentsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const [acceptTarget, setAcceptTarget] = useState<HrDocumentItem | null>(null);
  const [digits, setDigits] = useState('');
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(false);

  const [uploadTarget, setUploadTarget] = useState<HrDocumentItem | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await hrDocuments(nip);
      setData(res);
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) toast.error(t('hr.documents.loadError'));
    } finally {
      setLoading(false);
    }
  }, [nip, hrDocuments]);

  useEffect(() => { refresh(); }, [refresh]);

  const openAcceptDialog = (item: HrDocumentItem) => {
    setDigits('');
    setLocked(false);
    setAcceptTarget(item);
  };

  const handleAccept = async () => {
    if (!acceptTarget || !/^\d{4}$/.test(digits)) return;
    setBusy(true);
    try {
      await hrAcceptDocument(nip, acceptTarget.acceptanceId!, digits);
      setAcceptTarget(null);
      setDigits('');
      toast.success(t('hr.documents.acceptSuccess'));
      await refresh();
    } catch (error) {
      if (error instanceof SessionExpiredError) {
        // handled globally
      } else {
        const status = (error as Error & { status?: number }).status;
        if (status === 423) {
          setLocked(true);
        } else {
          toast.error((error as Error).message || t('hr.documents.acceptError'));
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const openUploadDialog = (item: HrDocumentItem) => {
    setUploadFile(null);
    setUploadTarget(item);
  };

  const handleUploadSigned = async () => {
    if (!uploadTarget || !uploadFile) return;
    const fd = new FormData();
    fd.append('file', uploadFile);
    setUploading(true);
    try {
      await uploadSignedDocument(nip, uploadTarget.acceptanceId!, fd);
      setUploadTarget(null);
      setUploadFile(null);
      toast.success(t('hr.documents.uploadSuccess'));
      await refresh();
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) {
        toast.error((error as Error).message || t('hr.documents.uploadError'));
      }
    } finally {
      setUploading(false);
    }
  };

  if (hrMeLoading || loading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  const pending = data?.pending ?? [];
  const done = data?.done ?? [];
  const library = data?.library ?? [];

  return (
    <div className="flex flex-col gap-4">
      <HrTabs nip={nip} hrMe={hrMe} />

      <Card>
        <CardHeader>
          <CardTitle>{t('hr.documents.pendingTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {pending.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('hr.documents.emptyPending')}</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {pending.map((item) => (
                <Card key={item.acceptanceId} className="gap-2 py-4">
                  <CardHeader className="gap-1 px-4">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-sm leading-tight font-semibold">{item.title}</CardTitle>
                      <FileText className="text-muted-foreground size-4 shrink-0" />
                    </div>
                    <p className="text-muted-foreground font-mono text-xs">{item.number}</p>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-3 px-4">
                    <DeadlineBadge deadline={item.deadline} />
                    <div className="flex flex-wrap items-center gap-2">
                      {item.acceptanceMode === 'electronic' && (
                        <Button size="sm" onClick={() => openAcceptDialog(item)}>
                          {t('hr.documents.accept')}
                        </Button>
                      )}
                      {item.acceptanceMode === 'physical' && item.status === 'pending' && (
                        <Button size="sm" variant="outline" onClick={() => openUploadDialog(item)}>
                          <UploadCloud className="size-3.5" />
                          {t('hr.documents.uploadSigned')}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDownload(nip, item, hrDocumentDownload)}
                      >
                        <Download className="size-3.5" />
                        {t('hr.documents.download')}
                      </Button>
                    </div>
                    {item.acceptanceMode === 'physical' && item.status === 'pending' && (
                      <p className="text-muted-foreground text-xs">{t('hr.documents.signInstruction')}</p>
                    )}
                    {item.acceptanceMode === 'physical' && item.status === 'returned' && (
                      <Badge variant="outline" className="border-sky-500/40 bg-sky-500/10 font-normal text-sky-700 dark:text-sky-400">
                        {t('hr.documents.returnedNote')}
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('hr.documents.doneTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {done.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('hr.documents.emptyDone')}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-muted-foreground w-28">{t('documents.publications.number')}</TableHead>
                    <TableHead className="text-muted-foreground">{t('documents.templates.name')}</TableHead>
                    <TableHead className="text-muted-foreground w-32">{t('documents.publications.method')}</TableHead>
                    <TableHead className="text-muted-foreground w-40">{t('documents.publications.acceptedAt')}</TableHead>
                    <TableHead className="text-muted-foreground w-14" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {done.map((item) => (
                    <TableRow key={item.acceptanceId}>
                      <TableCell className="py-2 font-mono text-xs">{item.number}</TableCell>
                      <TableCell className="py-2 font-medium">{item.title}</TableCell>
                      <TableCell className="py-2 text-sm">
                        {item.acceptanceMode === 'physical'
                          ? t('hr.documents.methods.physical')
                          : t('hr.documents.methods.electronic')}
                      </TableCell>
                      <TableCell className="text-muted-foreground py-2 text-sm">
                        {item.acceptedAt ? new Date(item.acceptedAt).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell className="py-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          onClick={() => openDownload(nip, item, hrDocumentDownload)}
                        >
                          <Download className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('hr.documents.libraryTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {library.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('hr.documents.emptyLibrary')}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-muted-foreground w-28">{t('documents.publications.number')}</TableHead>
                    <TableHead className="text-muted-foreground">{t('documents.templates.name')}</TableHead>
                    <TableHead className="text-muted-foreground w-40">{t('documents.publications.publishedAt')}</TableHead>
                    <TableHead className="text-muted-foreground w-14" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {library.map((item) => (
                    <TableRow key={item.publicationId}>
                      <TableCell className="py-2 font-mono text-xs">{item.number}</TableCell>
                      <TableCell className="py-2 font-medium">{item.title}</TableCell>
                      <TableCell className="text-muted-foreground py-2 text-sm">
                        {new Date(item.publishedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="py-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          onClick={() => openDownload(nip, item, hrDocumentDownload)}
                        >
                          <Download className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Accept dialog — mirrors MyDataView's PESEL reveal dialog exactly. */}
      <Dialog open={acceptTarget !== null} onOpenChange={(open) => !open && setAcceptTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('hr.documents.acceptTitle')}</DialogTitle>
            <DialogDescription>
              {t('hr.documents.acceptHint', { title: acceptTarget?.title ?? '' })}
            </DialogDescription>
          </DialogHeader>
          {locked ? (
            <p className="text-sm text-rose-600 dark:text-rose-400">{t('hr.documents.acceptLocked')}</p>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="accept-pesel-last4">{t('hr.documents.acceptInput')}</Label>
              <Input
                id="accept-pesel-last4"
                inputMode="numeric"
                maxLength={4}
                autoComplete="off"
                value={digits}
                onChange={(e) => setDigits(e.target.value.replace(/\D/g, '').slice(0, 4))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !busy && /^\d{4}$/.test(digits)) {
                    e.preventDefault();
                    handleAccept();
                  }
                }}
              />
            </div>
          )}
          <p className="text-muted-foreground text-xs">{t('hr.documents.acceptNote')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptTarget(null)}>
              {t('common.cancel')}
            </Button>
            {!locked && (
              <Button disabled={busy || !/^\d{4}$/.test(digits)} onClick={handleAccept}>
                {busy && <Loader2 className="size-4 animate-spin" />}
                {t('hr.documents.acceptSubmit')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload-back dialog: physical-mode signed scan, routed by acceptance id. */}
      <Dialog open={uploadTarget !== null} onOpenChange={(open) => !open && !uploading && setUploadTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('hr.documents.uploadSigned')}</DialogTitle>
            <DialogDescription>{t('hr.documents.signInstruction')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="hr-signed-file">{t('hr.documents.uploadFile')}</Label>
            <input
              id="hr-signed-file"
              type="file"
              accept="application/pdf,image/*"
              className="text-sm file:mr-3 file:rounded-md file:border file:px-3 file:py-1.5 file:text-sm"
              onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadTarget(null)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={uploading || !uploadFile} onClick={handleUploadSigned}>
              {uploading && <Loader2 className="size-4 animate-spin" />}
              {t('hr.documents.uploadSubmit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
